import styled, { css } from 'styled-components';
import { Icon, VideoPlayer as _VideoPlayer } from 'src/components';

export const Separator = styled.div(
  ({ theme }) => css`
    background: ${theme.co.neutral90};
    height: ${theme.sz.s05};
    width: 100%;
  `,
);

export const SeparatorWithSpacing = styled.div(
  ({ theme }) => css`
    margin: ${theme.sz.s4} 0;
  `,
);

export const Container = styled.div(
  ({ theme }) => css`
    display: grid;
    gap: ${theme.sz.s4};
    max-width: ${theme.sz.xxl};
    width: 100%;
    min-width: 0;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'info';

    @media (min-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s8};
    }
  `,
);

export const VideoContainer = styled.div<{ $objectPosition?: string }>(
  ({ $objectPosition }) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100dvh;
    /* Keep behind session UI; positive z-index covers the control strip. */
    z-index: -1;
    overflow: hidden;
    background-color: #000;
    --video-object-position: ${$objectPosition ?? '50% 0%'};
  `,
);

export const VideoOverlay = styled.div(
  () => css`
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.58) 0%,
      rgba(0, 0, 0, 0.28) 36%,
      rgba(0, 0, 0, 0.12) 100%
    );
  `,
);

/** Soft full-bleed fill behind the sharp video — cinematic side flou. */
export const VideoBackdropLayer = styled.div(
  () => css`
    position: absolute;
    inset: 0;
    pointer-events: none;
  `,
);

export const VideoBackdrop = styled(_VideoPlayer)(
  () => css`
    display: block;
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
      object-fit: cover;
      object-position: var(--video-object-position, 50% 0%);
      border-radius: 0;
      filter: blur(2.5rem) brightness(0.72);
      /* Scale past the edges so the blur doesn't fade into a hard matte. */
      transform: scale(1.14);
      transform-origin: center center;
    }
  `,
);

/**
 * Sharp subject layer: contain keeps the native framing (zoom-out vs cover)
 * and uses the full viewport height; horizontal gaps show the blurred backdrop.
 */
export const VideoPlayer = styled(_VideoPlayer)(
  () => css`
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
      object-fit: contain;
      object-position: var(--video-object-position, 50% 0%);
      border-radius: 0;
    }
  `,
);

export const InfoContainer = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    grid-area: info;
    font-size: ${theme.DEPRECATED_ty.small};
    min-width: 0;

    ${
      $onVideo &&
      css`
        /* Match LearningToolsPanel overlay padding so word copy lines up with "Learning Tools". */
        padding-inline-start: ${theme.sz.s4};
        color: ${theme.co.neutral99};
        text-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.45);

        label,
        button {
          color: ${theme.co.neutral90};
          font-size: ${theme.DEPRECATED_ty.small};
        }

        /* The video treatment lightens every button and adds a text shadow.
           Keep the correction chip and its actions readable on the light pink. */
        [data-testid='alert-wrapper'] {
          text-shadow: none;
        }

        [data-testid='alert-wrapper'] button {
          color: ${theme.co.secondary0};
        }

        h2,
        h3,
        h4 {
          color: ${theme.co.neutral99};
        }

        [data-testid='alert-wrapper'] h3 {
          color: inherit;
          font-size: ${theme.DEPRECATED_ty.smaller};
        }

        h2 {
          font-size: ${theme.DEPRECATED_ty.displaySmall};
        }

        h3 {
          font-size: ${theme.DEPRECATED_ty.largerStill};
        }

        h4 {
          font-size: ${theme.DEPRECATED_ty.mediumLarge};
        }

        [data-testid='phoneticSpelling'] {
          color: ${theme.co.neutral90};
          font-size: ${theme.DEPRECATED_ty.medium};
        }

        /* Keep more of the speaker visible on phones. */
        @media (max-width: ${theme.sz.md}) {
          h2 {
            font-size: ${theme.DEPRECATED_ty.largest};
          }

          h3 {
            font-size: ${theme.DEPRECATED_ty.larger};
          }

          h4 {
            font-size: ${theme.DEPRECATED_ty.medium};
          }

          [data-testid='phoneticSpelling'] {
            font-size: ${theme.DEPRECATED_ty.small};
          }
        }
      `
    }
  `,
);

export const Label = styled.label<{ $clickable?: boolean }>(
  ({ theme, $clickable }) => css`
    background: none;
    border: none;
    color: ${theme.co.secondary20};
    display: block;
    font-family: inherit;
    font-size: ${theme.DEPRECATED_ty.smaller};
    font-weight: ${theme.DEPRECATED_ty.semiBold};
    hyphens: auto;
    margin: 0 0 ${theme.sz.s4} 0;
    overflow-wrap: anywhere;
    padding: 0;
    text-align: inherit;
    text-transform: uppercase;

    &:first-of-type {
      margin: 0 0 ${theme.sz.s2} 0;
    }

    &:last-of-type {
      margin: ${theme.sz.s2} 0 ${theme.sz.s2} 0;
    }

    ${
      $clickable &&
      css`
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }

        &:focus-visible {
          outline: 2px solid ${theme.co.secondary20};
          outline-offset: 2px;
        }
      `
    }
  `,
);

export const H2Value = styled.h2<{ $showBigger: boolean }>(
  ({ theme, $showBigger }) => css`
    font-size: ${$showBigger ? theme.DEPRECATED_ty.displaySmall : theme.DEPRECATED_ty.largest};
    font-weight: ${({ theme }) => theme.DEPRECATED_ty.semiBold};
    hyphens: auto;
    line-height: 1.2;
    margin: 0;
    overflow-wrap: anywhere;
  `,
);

export const H3Value = styled.h3<{ $showBigger: boolean }>(
  ({ theme, $showBigger }) => css`
    font-size: ${$showBigger ? theme.DEPRECATED_ty.largerStill : theme.DEPRECATED_ty.larger};
    font-weight: ${({ theme }) => theme.DEPRECATED_ty.semiBold};
    hyphens: auto;
    line-height: 1.2;
    margin: 0;
    overflow-wrap: anywhere;
  `,
);

export const H4Value = styled.h4<{ $showBigger: boolean }>(
  ({ theme, $showBigger }) => css`
    font-size: ${$showBigger ? theme.DEPRECATED_ty.mediumLarge : theme.DEPRECATED_ty.medium};
    font-weight: ${theme.DEPRECATED_ty.mediumWeight};
    hyphens: auto;
    line-height: 1.5;
    margin: 0;
    overflow-wrap: anywhere;
  `,
);

export const LetterBreakdownContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.sz.s2};
    margin-top: ${theme.sz.s2};
  `,
);

export const LetterBreakdownText = styled.div(
  ({ theme }) => css`
    color: ${theme.co.secondary60};
    font-size: ${theme.DEPRECATED_ty.large};
    font-weight: ${theme.DEPRECATED_ty.mediumWeight};
  `,
);

export const TextAlternatives = styled.div(
  ({ theme }) => css`
    color: ${theme.co.secondary60};
    font-size: ${theme.DEPRECATED_ty.small};
    font-weight: ${theme.DEPRECATED_ty.mediumWeight};
    hyphens: auto;
    line-height: 1.5;
    overflow-wrap: anywhere;
  `,
);

export const PhoneticSpelling = styled(TextAlternatives)(
  ({ theme }) => css`
    color: ${theme.co.neutral70};
    hyphens: manual;
    margin-top: ${theme.sz.s1};
  `,
);

export const Image = styled.img(
  ({ theme }) => css`
    border-radius: ${theme.bo.small};
    margin: 0;
    max-width: ${theme.sz.s64};
    width: 100%;
  `,
);

export const AudioContainer = styled.div(
  ({ theme }) => css`
    background: ${theme.co.neutral98};
    border-radius: ${theme.bo.medium};
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: ${theme.sz.s2};
    width: fit-content;
  `,
);

// Negative bottom margin so the margin is only between word
// wrapped rows
export const AttributesContainer = styled.div(
  ({ theme }) => css`
    margin-bottom: -${theme.sz.s1};
  `,
);

// On UGC courses there can be lots of attributes with quite long
// copy, the inline-block makes them use their natural size and
// word-wrap (both between pills and inside the pill).
export const Attribute = styled.div(
  ({ theme }) => css`
    display: inline-block;
    margin-bottom: ${theme.sz.s1};
    margin-right: ${theme.sz.s1};
  `,
);

export const IncorrectAnswerContent = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
  `,
);

export const IncorrectAnswerText = styled.div(
  () => css`
    flex: 1;
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  `,
);

export const ActuallyRightContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.sz.s05};
    flex-shrink: 0;
  `,
);

export const SavedBadge = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxxs};
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: ${theme.sz.s1};
    padding: ${theme.sz.s05} ${theme.sz.s2};
    border: ${theme.bo.thinWidth} solid ${theme.co.primary20};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary95};
    color: ${theme.co.neutral10};
    font-size: ${theme.sz.s3};
  `,
);

export const SavedIcon = styled(Icon)(
  ({ theme }) => css`
    color: ${theme.co.primary0};
    font-size: ${theme.sz.s3};
  `,
);
